/**
 * The −/+ number control. The single most-tapped thing in the app: every
 * disconnect count, every fixture count, every landing.
 *
 * Presentational and dumb (brief, task B). It does not know what a disconnect
 * is, it does not clamp against a car count — a ceiling that exists is a
 * validation rule and lives in src/core/validation.ts, which produces the chip
 * underneath this control. `max` here is only for structural limits the caller
 * genuinely owns.
 *
 * ── The readout is typeable (D-053) ─────────────────────────────────────────
 * Chip, testing production: *"be able to type in distance of FACP to MR. Right
 * now you have to hit up and down arrows. I just had to hit it 200 times"*,
 * and *"be able to type in the number of Landings served instead of +/-"*. So
 * the number between − and + is a box: tap it, the iPhone raises the digit pad
 * (`inputMode="numeric"`), type the whole number, and it is written on blur or
 * Enter — the draft pattern `LandingInput` (RangeField.tsx) already uses. The
 * −/+ buttons and their in-flight logic (D-037) are untouched. A typed number
 * is clamped into `min`/`max`; anything that is not a plain whole number
 * ("12.5", "-3", "1e3", empty) writes nothing and the box goes back. `parseTypedCount` is the whole rule,
 * pure, so the node suite can test it without a click.
 *
 * A typed number is written even when it EQUALS the readout. That is the
 * point for a `number` question nobody has answered yet: the readout says 0
 * while the document says nothing, so "0" typed over "0" is the surveyor's
 * first real answer, not a no-op (L3a, D-053 §5). Tapping in and out without
 * typing writes nothing.
 *
 * Read-only renders the plain readout and no box at all — a submitted survey
 * offers nothing to type into.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

/** How long a sent number outranks the prop — longer than any IndexedDB write, shorter than a second thought. */
const SENT_TTL_MS = 1000;

/**
 * The number a typed draft writes, or `undefined` for "write nothing".
 *
 *  - empty, or not a whole number: nothing — the box goes back to `value`;
 *  - otherwise the number, clamped into `[min, max]`;
 *  - …unless nothing was typed and that number is `value` already: a tap in
 *    and out is not an answer. A blank stepper (`value` undefined) has no
 *    number to equal.
 *
 * `edited` is what lets "0" over a readout of 0 through: see the header.
 */
export function parseTypedCount(
  draft: string,
  { value, edited, min = 0, max }: { value: number | undefined; edited: boolean; min?: number; max?: number },
): number | undefined {
  const text = draft.trim();
  if (!/^\d+$/.test(text)) return undefined;
  let next = Number.parseInt(text, 10);
  if (!Number.isSafeInteger(next)) return undefined;
  if (next < min) next = min;
  if (max !== undefined && next > max) next = max;
  if (!edited && next === value) return undefined;
  return next;
}

export interface StepperProps {
  /**
   * The stored number, or `undefined` for nothing stored yet — a BLANK box
   * (D-055, D-056). For a count whose floor is 1 (G9a, G14a, A3a, A2) an
   * unanswered "0" is a number the control cannot produce; blank says what is
   * true: not answered. − is dead, + writes the floor (or 1 when the floor is
   * 0), and a typed number writes itself. One fact, so no caller can pass
   * "blank" beside a number that contradicts it.
   */
  value: number | undefined;
  /**
   * Persist it. Return `false` to say the number was NOT written (a guard
   * refused it) — the stepper then forgets it and the next tap steps from
   * `value` again. Returning `void` (or `true`) means it was accepted.
   */
  onChange: (next: number) => boolean | void;
  /** Visible label. Omit for a bare stepper and pass `ariaLabel` instead. */
  label?: ReactNode;
  /** Defaults to 0: no §3 quantity is meaningfully negative. */
  min?: number;
  max?: number;
  step?: number;
  /** Accessible name when there is no visible label. */
  ariaLabel?: string;
  disabled?: boolean;
  /** Write a typed, unblurred number when the stepper goes away. Default true; see the unmount note. */
  commitDraftOnUnmount?: boolean;
}

export function Stepper({
  value,
  onChange,
  label,
  min = 0,
  max,
  step = 1,
  ariaLabel,
  disabled = false,
  commitDraftOnUnmount = true,
}: StepperProps): ReactNode {
  const shown = value === undefined ? '' : String(value);
  const atMin = value === undefined || value <= min;
  const atMax = value !== undefined && max !== undefined && value >= max;
  const name = ariaLabel ?? (typeof label === 'string' ? label : 'value');

  /*
   * Taps in flight — D-037. The readout is the document's number, which
   * arrives one write behind the tap (SurveyProvider rule 1). Two taps inside
   * that window both read the same `value` and both send the same number, and
   * the second tap is swallowed. So a tap steps from the LAST NUMBER SENT
   * while that send is still live. Refusal is not guessed by clock: a guard
   * (the Building screen's B12 confirm) says so directly by returning
   * `false`, and that number is simply never remembered — it never became
   * `sent`, so there is nothing to forget. An EARLIER accepted send already
   * in flight is untouched by a later refusal; the next tap still steps from
   * it. `SENT_TTL_MS` is only a backstop for a write that DID land but at a
   * value other than the one sent (a clamp downstream), so an accepted send
   * does not outlive the write it describes. The readout itself stays the
   * document's: nothing here is shown before it is saved.
   */
  const sent = useRef<{ value: number; at: number } | null>(null);
  useEffect(() => {
    if (sent.current?.value === value) sent.current = null;
  }, [value]);
  /** Write it; true when it was accepted, false when a guard refused it. */
  const send = (next: number): boolean => {
    const accepted = onChange(next) !== false;
    if (accepted) sent.current = { value: next, at: Date.now() };
    return accepted;
  };
  /*
   * A typed number a guard refused (B12's confirm, asking about it right now)
   * — when. The blur that sent it lands a moment before the click on −/+ that
   * caused the blur, and that click must not step from `value` as if nothing
   * were being asked: 3, type 2, tap + would write 4 under a dialog about 2.
   * So THAT click does nothing. Bounded by SENT_TTL_MS.
   *
   * Only that click (D-056). Before, any tap inside the second was dropped —
   * type 2, Enter (refused), then a deliberate + went nowhere. The click that
   * caused the blur is the one whose press began while the box still had
   * focus: a pointer press always comes before the blur it causes, so
   * `pressedFromBox` is read off `pointerdown`. A press that starts with the
   * box already blurred (after Enter), or a keyboard-activated button (no
   * pointer at all), is a deliberate tap. A keyboard click is told by its
   * `detail` of 0 (a pointer click counts its clicks from 1), so a press
   * that slid off without clicking cannot lend it its `pressedFromBox`.
   */
  const refusedAt = useRef<number | null>(null);
  const boxRef = useRef<HTMLInputElement | null>(null);
  const pressedFromBox = useRef(false);
  const notePress = (): void => {
    pressedFromBox.current = boxRef.current !== null && document.activeElement === boxRef.current;
  };
  const tap = (direction: -1 | 1, clickCount: number): void => {
    // A −/+ tapped while the box still has focus (iOS does not move focus to
    // a tapped button, so no blur has written the draft): a number the
    // surveyor typed is where the step starts, and an untyped draft is
    // dropped — left in place, its blur would write the OLD number back over
    // this tap.
    const typed = takeDraft();
    const refused = refusedAt.current;
    refusedAt.current = null;
    const causedTheBlur = pressedFromBox.current && clickCount > 0;
    pressedFromBox.current = false;
    if (typed === undefined && causedTheBlur && refused !== null && Date.now() - refused < SENT_TTL_MS) return;
    const last = sent.current;
    const from = typed ?? (last && Date.now() - last.at < SENT_TTL_MS ? last.value : value);
    if (from === undefined) {
      // Blank: − has nothing to step down from; + is the first answer, which
      // is never under the floor (a floor of 3 writes 3, not 1).
      if (direction > 0) send(max === undefined ? Math.max(min, step) : Math.min(max, Math.max(min, step)));
      return;
    }
    const next =
      direction < 0
        ? Math.max(min, from - step)
        : max === undefined
          ? from + step
          : Math.min(max, from + step);
    send(next);
  };

  /*
   * The typed draft (D-053). `null` while the box is not being edited: it
   * then shows the document's number, so a −/+ tap or a write from elsewhere
   * is never hidden behind a stale draft. The draft is the RAW text — "12.5",
   * "-3", "1e3" stay exactly what was typed, and `parseTypedCount` refuses
   * them, so the box goes back to the stored number rather than any of them
   * quietly becoming 125, 3 or 13. A typed number goes through `send`, so a +
   * tapped straight after it steps from it (D-037). Mirrored in a ref so the
   * unmount below can read it.
   */
  const [draft, setDraftState] = useState<string | null>(null);
  const draftRef = useRef<string | null>(null);
  const setDraft = (next: string | null): void => {
    draftRef.current = next;
    setDraftState(next);
  };
  const edited = useRef(false);
  /** Ends the edit; the number it would write, if the surveyor typed one. */
  function takeDraft(): number | undefined {
    // Untyped, the draft is only the readout copied at focus — possibly stale
    // by now — so it is never a number to write or to step from.
    const text = draftRef.current;
    const next =
      text === null || !edited.current
        ? undefined
        : parseTypedCount(text, { value, edited: true, min, max });
    edited.current = false;
    setDraft(null);
    return next;
  }
  const commitDraft = (): void => {
    const next = takeDraft();
    if (next !== undefined && !send(next)) refusedAt.current = Date.now();
  };

  /*
   * Gone without a blur — the screen changed under a focused box (iOS can
   * navigate, or a Yes/No can hide the question, with the keyboard still
   * up). A number the surveyor typed is written on the way out, through the
   * props this instance last rendered with, so it lands where it was typed.
   * `commitDraftOnUnmount={false}` opts out: a flagCount's "How many?" goes
   * when its own No is tapped, and writing the count then would put the Yes
   * back.
   */
  const latest = useRef({ value, min, max, onChange, commitDraftOnUnmount });
  latest.current = { value, min, max, onChange, commitDraftOnUnmount };
  useEffect(
    () => () => {
      const text = draftRef.current;
      const last = latest.current;
      if (text === null || !edited.current || !last.commitDraftOnUnmount) return;
      const next = parseTypedCount(text, { value: last.value, edited: true, min: last.min, max: last.max });
      if (next !== undefined) last.onChange(next);
    },
    [],
  );

  const readout = disabled ? (
    /* aria-live so the new count is spoken after a tap — the surveyor is not
     * looking at the screen while counting disconnects. */
    <output className="stepper__value" aria-live="polite">
      {shown}
    </output>
  ) : (
    <>
      <input
        ref={boxRef}
        className="stepper__value stepper__input"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        enterKeyHint="done"
        autoComplete="off"
        aria-label={`${name}: type a number`}
        value={draft ?? shown}
        onFocus={(event) => {
          // Select the stored digits so what is typed REPLACES them — the
          // `LandingInput` lesson: iOS drops a synchronous select() from the
          // focus handler, so it waits a frame, and only if focus stayed.
          const el = event.currentTarget;
          edited.current = false;
          // A new edit: an earlier refusal no longer explains any click.
          refusedAt.current = null;
          setDraft(shown);
          requestAnimationFrame(() => {
            if (document.activeElement === el) el.select();
          });
        }}
        // `onInput`, not `onChange`, is what says "the surveyor typed": React
        // swallows a change whose value equals the last one, so "0" typed
        // over a selected "0" — the L3a case — never reaches onChange. The
        // end-to-end run caught it; the native input event always fires.
        onInput={() => {
          edited.current = true;
        }}
        onChange={(event) => {
          edited.current = true;
          setDraft(event.target.value);
        }}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          // Blur only — its own handler writes. Writing here too would be two
          // writes for one Enter.
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
      />
      {/* The spoken count, as before: the box is what the eye reads, this is
       * what a screen reader hears after a tap. */}
      <output className="sr-only" aria-live="polite">
        {shown}
      </output>
    </>
  );

  const control = (
    <div className="stepper" role="group" aria-label={name}>
      <button
        type="button"
        className="stepper__btn"
        onPointerDown={notePress}
        onClick={(event) => tap(-1, event.detail)}
        disabled={disabled || atMin}
        aria-label={`${name}: one less`}
      >
        −
      </button>
      {readout}
      <button
        type="button"
        className="stepper__btn"
        onPointerDown={notePress}
        onClick={(event) => tap(1, event.detail)}
        disabled={disabled || atMax}
        aria-label={`${name}: one more`}
      >
        +
      </button>
    </div>
  );

  if (label === undefined) return control;

  return (
    <div className="stepper-row">
      <span className="stepper-row__label">{label}</span>
      {control}
    </div>
  );
}
